// File Path: src/pages/ServicePages/CorporateEventsPage.jsx

import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import ContactWidget from '../../components/ui/ContactWidget';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ClientLogos from 'pages/homepage/components/ClientLogos';
import CorporateQuoteModal from '../../components/ui/CorporateQuoteModal.jsx'; // This import path must be correct

// Reusable Gallery Image Component
const GalleryImage = ({ src, alt }) => (
  <div className="overflow-hidden rounded-xl group shadow-sm hover:shadow-lg transition-shadow duration-300">
    <img 
      src={src} 
      alt={alt}
      className="w-full h-full object-cover aspect-[4/3] group-hover:scale-105 transition-transform duration-500 ease-in-out" 
      loading="lazy"
    />
  </div>
);

// Reusable FAQ Item Component
const FaqItem = ({ question, children }) => (
  <details className="p-6 rounded-lg bg-white border border-gray-200/80 cursor-pointer group transition-colors hover:bg-gray-50">
    <summary className="font-semibold text-lg flex items-center justify-between">
      {question}
      <Icon name="ChevronDown" className="h-5 w-5 transition-transform duration-300 group-open:rotate-180" />
    </summary>
    <div className="text-gray-600 mt-4 leading-relaxed">
      {children}
    </div>
  </details>
);

const CorporateEventsPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const galleryItems = [
    { src: '/gallery/cop1.jpg', alt: 'Large conference hall setup for a corporate summit in Nairobi by Luxe & Allure.' },
    { src: '/gallery/cop2.jpg', alt: 'Elegant gala dinner tables set for a corporate awards ceremony in Kenya.' },
    { src: '/gallery/cop3.jpg', alt: 'Dynamic stage with LED screens for a tech product launch event in Nairobi.' },
    { src: '/gallery/cop4.jpg', alt: 'Professionals networking at a business event planned by Luxe & Allure.' },
    { src: '/gallery/cop5.jpg', alt: 'Immersive brand activation booth designed for an expo in Kenya.' },
    { src: '/gallery/cop6.jpg', alt: 'Engaging corporate team-building activity organized outdoors near Nairobi.' },
    { src: '/gallery/cop7.jpg', alt: 'Professionally lit stage for a corporate awards night in Nairobi.' },
    { src: '/gallery/cop8.jpg', alt: 'Efficient delegate registration desk at a conference in Nairobi.' },
  ];
  
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "Luxe & Allure Corporate Event Planning",
    "image": "https://www.yourwebsite.com/gallery/event.jpg",
    "@id": "https://www.yourwebsite.com/services/corporate-events",
    "url": "https://www.yourwebsite.com/services/corporate-events",
    "telephone": "+254727937010",
    "priceRange": "$$$",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "123 Business Avenue",
      "addressLocality": "Nairobi",
      "addressCountry": "KE"
    },
    "description": "Premier corporate event planning services in Nairobi, Kenya. We specialize in creating high-impact conferences, product launches, and gala dinners that deliver on business objectives.",
    "serviceType": "Corporate Event Planner",
    "areaServed": {
      "@type": "Country",
      "name": "Kenya"
    },
    "provider": {
        "@type": "Organization",
        "name": "Luxe & Allure"
    }
  };

  return (
    <>
      <div className="min-h-screen bg-slate-50 text-gray-800">
        <Helmet>
          <title>Top Corporate Event Planners in Nairobi, Kenya | Luxe & Allure</title>
          <meta name="description" content="Partner with Luxe & Allure, the leading corporate event planners in Nairobi. We deliver flawless conferences, product launches, galas, and brand activations that elevate your brand." />
          <link rel="canonical" href="https://www.yourwebsite.com/services/corporate-events" />
          <meta name="keywords" content="corporate event planners nairobi, conference planning kenya, product launch events, gala dinner organizers, brand activation kenya, business events nairobi, top event companies kenya" />
          <meta property="og:title" content="Top Corporate Event Planners in Nairobi, Kenya | Luxe & Allure" />
          <meta property="og:description" content="We deliver flawless conferences, product launches, and brand activations. Partner with Nairobi's leading event management company." />
          <meta property="og:image" content="https://www.yourwebsite.com/gallery/corporate/gala-dinner.jpg" />
          <meta property="og:url" content="https://www.yourwebsite.com/services/corporate-events" />
          <meta property="og:type" content="website" />
          <meta property="og:site_name" content="Luxe & Allure" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Top Corporate Event Planners in Nairobi, Kenya | Luxe & Allure" />
          <meta name="twitter:description" content="We deliver flawless conferences, product launches, and brand activations. Partner with Nairobi's leading event management company." />
          <meta name="twitter:image" content="https://www.yourwebsite.com/gallery/corporate/gala-dinner.jpg" />
          <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
        </Helmet>

        <Header />
        <ContactWidget />

        <main className="font-sans">
          {/* --- Hero Section --- */}
          <section className="bg-white">
              <div className="container mx-auto px-6 lg:px-8 py-20 lg:py-24">
                  <div className="grid lg:grid-cols-2 gap-12 items-center">
                      <div className="text-center lg:text-left">
                          <Breadcrumb />
                          <h1 className="font-serif text-5xl md:text-6xl font-bold leading-tight tracking-tight mt-4">
                              Top Corporate Event Planners in Nairobi
                          </h1>
                          <p className="mt-6 text-lg text-gray-600 max-w-xl mx-auto lg:mx-0">
                              We design and execute strategic corporate events that inspire audiences, amplify brand messages, and deliver measurable results in Kenya and beyond.
                          </p>
                          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                              <Button variant="primary" size="lg" onClick={() => setIsModalOpen(true)}>
                                  Request a Proposal
                              </Button>
                              <Button variant="outline" size="lg" onClick={() => document.getElementById('gallery-section')?.scrollIntoView({ behavior: 'smooth' })}>
                                  See Our Work
                                  <Icon name="ArrowDown" className="ml-2 w-4 h-4" />
                              </Button>
                          </div>
                      </div>
                      <div className="h-80 lg:h-full w-full">
                          <img 
                              src="/gallery/event.jpg" 
                              alt="A professional and engaging corporate event in Nairobi planned by Luxe & Allure."
                              className="w-full h-full object-cover rounded-xl shadow-lg" 
                          />
                      </div>
                  </div>
              </div>
          </section>

          {/* All other sections remain unchanged */}
          <section className="py-20 lg:py-28">
            <div className="container mx-auto px-6 lg:px-8 text-center max-w-4xl">
              <h2 className="font-serif text-4xl font-bold text-gray-900">
                Your Strategic Partner in Corporate Event Management
              </h2>
              <p className="mt-6 text-lg text-gray-600 leading-relaxed">
               In today's competitive landscape, a corporate event is a powerful communication tool. It's an opportunity to build relationships, showcase innovation, and reinforce your brand's prestige. At Luxe & Allure, we are strategic partners who understand this. We go beyond logistics to create immersive experiences that are meticulously designed to align with your business goals, engage your audience, and leave a lasting, positive impact. Our expertise in Nairobi's corporate scene ensures flawless execution every time.
              </p>
            </div>
          </section>

          <section className="py-20 lg:py-28 bg-white">
            <div className="container mx-auto px-6 lg:px-8">
              <div className="text-center mb-16">
                <h2 className="font-serif text-4xl font-bold text-gray-900">
                  Full-Service Planning for Every Corporate Milestone
                </h2>
                <p className="mt-4 text-lg text-gray-600">Our event management expertise spans the full spectrum of corporate gatherings in Kenya.</p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                <div className="text-center p-6">
                  <div className="flex items-center justify-center w-16 h-16 mx-auto bg-primary/10 rounded-full mb-4">
                     <Icon name="Users" className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="font-bold text-xl mb-2">Conference & Summit Planning</h3>
                  <p className="text-gray-600">Seamless management for local and international gatherings, from delegate logistics to technical production.</p>
                </div>
                <div className="text-center p-6">
                  <div className="flex items-center justify-center w-16 h-16 mx-auto bg-primary/10 rounded-full mb-4">
                     <Icon name="Rocket" className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="font-bold text-xl mb-2">Product Launch Events</h3>
                  <p className="text-gray-600">High-impact events that generate buzz, capture media attention, and drive immediate market engagement.</p>
                </div>
                <div className="text-center p-6">
                  <div className="flex items-center justify-center w-16 h-16 mx-auto bg-primary/10 rounded-full mb-4">
                     <Icon name="Award" className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="font-bold text-xl mb-2">Gala Dinners & Awards Nights</h3>
                  <p className="text-gray-600">Elegant and prestigious evenings that celebrate success and honor achievement with sophistication and style.</p>
                </div>
                 <div className="text-center p-6">
                    <div className="flex items-center justify-center w-16 h-16 mx-auto bg-primary/10 rounded-full mb-4">
                       <Icon name="Target" className="w-8 h-8 text-primary" />
                    </div>
                    <h3 className="font-bold text-xl mb-2">Brand Activation Campaigns</h3>
                    <p className="text-gray-600">Creative and interactive experiences that connect your brand with your target audience in a memorable way.</p>
                </div>
              </div>
            </div>
          </section>

          <section id="gallery-section" className="py-20 lg:py-28">
            <div className="container mx-auto px-6 lg:px-8">
              <div className="text-center mb-16">
                <h2 className="font-serif text-4xl font-bold text-gray-900">
                  Our Corporate Event Portfolio
                </h2>
                <p className="mt-4 text-lg text-gray-600">A showcase of the precision and creativity we bring to corporate events in Nairobi.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {galleryItems.map((item, index) => (
                  <GalleryImage key={index} src={item.src} alt={item.alt} />
                ))}
              </div>
            </div>
          </section>

          <section className="py-20 lg:py-28 bg-white">
              <div className="container mx-auto px-6 lg:px-8">
                  <div className="text-center mb-16 max-w-3xl mx-auto">
                      <h2 className="font-serif text-4xl font-bold text-gray-900">
                        Corporate Event Planning FAQs
                      </h2>
                      <p className="mt-4 text-lg text-gray-600">Addressing key considerations for our corporate partners in Kenya.</p>
                  </div>
                  <div className="max-w-4xl mx-auto space-y-4">
                      <FaqItem question="How do you ensure our event aligns with our brand identity?">
                        <p>Brand alignment is central to our process. We start with a deep dive into your brand guidelines, messaging, and event objectives. Every element, from the initial invitation to the on-site branding and post-event communication, is crafted to be a seamless extension of your corporate identity.</p>
                      </FaqItem>
                      <FaqItem question="Can you manage logistics for international delegates?">
                        <p>Yes. We have extensive experience managing events with international attendees. Our services include airport transfers, accommodation booking, visa assistance communication, and creating welcome packs to ensure a smooth and welcoming experience for your guests arriving in Nairobi.</p>
                      </FaqItem>
                      <FaqItem question="How do you measure the success and ROI of an event?">
                        <p>We work with you to define key performance indicators (KPIs) from the outset. Success is measured against these goals, whether it's lead generation, media impressions, delegate satisfaction, or employee engagement. We provide comprehensive post-event reports and analysis to demonstrate a clear return on investment from your event in Nairobi.</p>
                      </FaqItem>
                  </div>
              </div>
          </section>
          
          <section id="contact-section" className="bg-primary text-white">
            <div className="container mx-auto px-6 lg:px-8 py-20 lg:py-24 text-center">
              <h2 className="font-serif text-4xl font-bold">
                Ready to Plan a Landmark Corporate Event?
              </h2>
              <p className="mt-4 max-w-xl mx-auto text-primary/80">
                Partner with Nairobi's trusted corporate event planners. Let's discuss your brief and develop a proposal that exceeds your expectations.
              </p>
              <Button variant="secondary" size="xl" className="mt-8" onClick={() => setIsModalOpen(true)}>
                  Request a Proposal
                  <Icon name="Send" className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </section>

          <ClientLogos />
        </main>
      </div>
      
      {isModalOpen && <CorporateQuoteModal onClose={() => setIsModalOpen(false)} />}
    </>
  );
};

export default CorporateEventsPage;